// Petit clavier à l'écran (une octave de touches blanches) pour le clavier d'essai et la
// source de synthèse : utilisable au doigt sur un téléphone, au clic, ou au clavier.

import { useState, type KeyboardEvent, type PointerEvent } from 'react';
import { inputManager } from '../../input/manager';
import { noteName } from '../../music/pitchNames';
import { useSettings } from '../hooks/stores';

const WHITE_OFFSETS = [0, 2, 4, 5, 7, 9, 11, 12];

export function MiniKeyboard({ base = 60 }: { base?: number }) {
  const settings = useSettings();
  const [down, setDown] = useState<Set<number>>(() => new Set());
  const pitches = WHITE_OFFSETS.map((o) => base + o);

  const press = (pitch: number) => {
    inputManager.keyboard.press(pitch);
    setDown((s) => new Set(s).add(pitch));
  };
  const release = (pitch: number) => {
    inputManager.keyboard.release(pitch);
    setDown((s) => {
      if (!s.has(pitch)) return s;
      const next = new Set(s);
      next.delete(pitch);
      return next;
    });
  };

  const onPointerDown = (pitch: number) => (e: PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // pointeur déjà libéré ou synthétique : la touche joue quand même
    }
    press(pitch);
  };
  const onPointerEnd = (pitch: number) => () => release(pitch);
  const onKeyDown = (pitch: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
      e.preventDefault();
      press(pitch);
    }
  };
  const onKeyUp = (pitch: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') release(pitch);
  };

  return (
    <div className="mini-keyboard" role="group" aria-label="Clavier d’essai à l’écran">
      {pitches.map((p) => (
        <button
          key={p}
          type="button"
          className={down.has(p) ? 'mini-key down' : 'mini-key'}
          onPointerDown={onPointerDown(p)}
          onPointerUp={onPointerEnd(p)}
          onPointerCancel={onPointerEnd(p)}
          onKeyDown={onKeyDown(p)}
          onKeyUp={onKeyUp(p)}
          onBlur={onPointerEnd(p)}
          aria-label={`Touche ${noteName(p, settings.naming)}`}
        >
          {noteName(p, settings.naming)}
        </button>
      ))}
    </div>
  );
}
